import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import BlockedContacts from "./BlockedContacts";
import "../styles/PrivacySettings.css";

function PrivacySettings({ user, onBack, onUserUpdate, socket }) {
  // State for active subsection
  const [activeSubsection, setActiveSubsection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [popupState, setPopupState] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Personal Info Settings
  const [lastSeenSetting, setLastSeenSetting] = useState("everyone");
  const [profilePhotoSetting, setProfilePhotoSetting] = useState("everyone");
  const [aboutSetting, setAboutSetting] = useState("everyone");
  const [statusSetting, setStatusSetting] = useState("everyone");
  const [readReceiptsSetting, setReadReceiptsSetting] = useState(true);

  // Disappearing Messages
  const [defaultMessageTimer, setDefaultMessageTimer] = useState("off");

  // Advanced Settings
  const [blockUnknownMessages, setBlockUnknownMessages] = useState(false);
  const [disableLinkPreviews, setDisableLinkPreviews] = useState(false);
  const [groupAddPermission, setGroupAddPermission] = useState("everyone");

  // Load privacy settings on component mount
  useEffect(() => {
    loadPrivacySettings();
  }, [user]);

  useEffect(() => {
    if (!popupState.show) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setPopupState((prev) => ({ ...prev, show: false }));
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [popupState.show]);

  const loadPrivacySettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.apiUrl}/api/auth/privacy-settings/${user.id}`
      );
      const settings = response.data.privacySettings;

      setLastSeenSetting(settings.lastSeenPrivacy || "everyone");
      setProfilePhotoSetting(settings.profilePhotoPrivacy || "everyone");
      setAboutSetting(settings.aboutPrivacy || "everyone");
      setStatusSetting(settings.statusPrivacy || "everyone");
      setReadReceiptsSetting(
        settings.readReceipts !== undefined ? settings.readReceipts : true
      );
      setDisableLinkPreviews(
        settings.disableLinkPreviews !== undefined ? settings.disableLinkPreviews : false
      );
    } catch (error) {
      console.error("Error loading privacy settings:", error);
      // Keep default values if loading fails
    } finally {
      setLoading(false);
    }
  };

  const privacyOptions = [
    { value: "everyone", label: "Everyone" },
    { value: "contacts", label: "My Contacts" },
    { value: "nobody", label: "Nobody" },
  ];

  const timerOptions = [
    { value: "off", label: "Off" },
    { value: "24h", label: "24 hours" },
    { value: "7d", label: "7 days" },
    { value: "90d", label: "90 days" },
  ];

  const groupOptions = [
    { value: "everyone", label: "Everyone" },
    { value: "contacts", label: "My Contacts" },
    { value: "nobody", label: "Nobody" },
  ];

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      // Save privacy settings to backend
      await axios.put(`${config.apiUrl}/api/auth/privacy-settings/${user.id}`, {
        profilePhotoPrivacy: profilePhotoSetting,
        lastSeenPrivacy: lastSeenSetting,
        aboutPrivacy: aboutSetting,
        statusPrivacy: statusSetting,
        readReceipts: readReceiptsSetting,
        disableLinkPreviews: disableLinkPreviews,
      });

      // TODO: Save other settings (message timer, block unknown, etc.)
      // //console.log("Privacy settings:", {
      //   lastSeen: lastSeenSetting,
      //   profilePhoto: profilePhotoSetting,
      //   about: aboutSetting,
      //   status: statusSetting,
      //   readReceipts: readReceiptsSetting,
      //   messageTimer: defaultMessageTimer,
      //   blockUnknown: blockUnknownMessages,
      //   disableLinkPreviews: disableLinkPreviews,
      //   groupPermission: groupAddPermission,
      // });

      // Update user object with new privacy settings
      const updatedUser = {
        ...user,
        privacySettings: {
          ...user.privacySettings,
          profilePhotoPrivacy: profilePhotoSetting,
          lastSeenPrivacy: lastSeenSetting,
          aboutPrivacy: aboutSetting,
          statusPrivacy: statusSetting,
          readReceipts: readReceiptsSetting,
          disableLinkPreviews: disableLinkPreviews,
        },
      };

      // Call the update callback if provided
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      setPopupState({
        show: true,
        message: "Privacy settings saved successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Error saving privacy settings:", error);
      setPopupState({
        show: true,
        message: "Failed to save privacy settings. Please try again.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBlockedContactsClick = () => {
    setActiveSubsection("blocked-contacts");
  };

  const handleBackToPrivacy = () => {
    setActiveSubsection(null);
  };

  // If viewing blocked contacts, show that component
  if (activeSubsection === "blocked-contacts") {
    return <BlockedContacts user={user} onBack={handleBackToPrivacy} socket={socket} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="privacy-settings">
        <div className="privacy-header">
          <button className="back-button" onClick={onBack}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Privacy</h2>
        </div>
        <div className="privacy-content">
          <div className="loading-privacy">
            <p>Loading privacy settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-settings">
      <div className="privacy-header">
        <button className="back-button" onClick={onBack}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Privacy</h2>
      </div>

      <div className="privacy-content">
        {popupState.show && (
          <div
            className={`privacy-popup privacy-popup-${popupState.type}`}
            role="status"
            aria-live="polite"
          >
            <span>{popupState.message}</span>
            <button
              type="button"
              className="privacy-popup-close"
              onClick={() => setPopupState((prev) => ({ ...prev, show: false }))}
              aria-label="Close notification"
            >
              x
            </button>
          </div>
        )}

        {/* Personal Info Section */}
        <div className="privacy-section">
          <h3>Who can see my personal info</h3>
          <p className="section-description">
            Changes to who can see your personal info won't affect your existing
            chats.
          </p>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Last seen and online</span>
              <span className="setting-description">
                If you don't share your Last Seen, you won't be able to see
                other people's Last Seen.
              </span>
            </div>
            <select
              value={lastSeenSetting}
              onChange={(e) => setLastSeenSetting(e.target.value)}
              className="setting-select"
            >
              {privacyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Profile photo</span>
              <span className="setting-description">
                Who can see your profile photo.
              </span>
            </div>
            <select
              value={profilePhotoSetting}
              onChange={(e) => setProfilePhotoSetting(e.target.value)}
              className="setting-select"
            >
              {privacyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">About</span>
              <span className="setting-description">
                Who can see your about info.
              </span>
            </div>
            <select
              value={aboutSetting}
              onChange={(e) => setAboutSetting(e.target.value)}
              className="setting-select"
            >
              {privacyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Status</span>
              <span className="setting-description">
                Who can see your status updates.
              </span>
            </div>
            <select
              value={statusSetting}
              onChange={(e) => setStatusSetting(e.target.value)}
              className="setting-select"
            >
              {privacyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Read receipts</span>
              <span className="setting-description">
                If turned off, you won't send or receive read receipts. Read
                receipts are always sent for group chats.
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={readReceiptsSetting}
                onChange={(e) => setReadReceiptsSetting(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        {/* Disappearing Messages Section */}
        <div className="privacy-section">
          <h3>Disappearing messages</h3>
          <p className="section-description">
            For enhanced privacy, you can set all new individual chats to
            disappear.
          </p>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Default message timer</span>
              <span className="setting-description">
                Start new chats with disappearing messages set to this timer.
              </span>
            </div>
            <select
              value={defaultMessageTimer}
              onChange={(e) => setDefaultMessageTimer(e.target.value)}
              className="setting-select"
            >
              {timerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Section */}
        <div className="privacy-section">
          <h3>Advanced</h3>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">
                Block unknown account messages
              </span>
              <span className="setting-description">
                Block messages from accounts not in your contacts.
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={blockUnknownMessages}
                onChange={(e) => setBlockUnknownMessages(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Disable link previews</span>
              <span className="setting-description">
                Links in messages won't generate previews.
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={disableLinkPreviews}
                onChange={(e) => setDisableLinkPreviews(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Groups</span>
              <span className="setting-description">
                Who can add you to groups.
              </span>
            </div>
            <select
              value={groupAddPermission}
              onChange={(e) => setGroupAddPermission(e.target.value)}
              className="setting-select"
            >
              {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div
            className="setting-item clickable"
            onClick={handleBlockedContactsClick}
          >
            <div className="setting-info">
              <span className="setting-label">Blocked contacts</span>
              <span className="setting-description">
                Manage your blocked contacts list.
              </span>
            </div>
            <div className="setting-arrow">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="privacy-actions">
          <button
            className="save-button"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrivacySettings;
